import type { WorkOrderTemplate } from '#/graphql/types';
import type { ColumnProps } from '#/common/components/DataGrid';
import { categories, executionIntervals, impacts, priorities, types } from '../../utils/constants';
import { gql } from '@apollo/client';
import {
  getAssetTypeList,
  getManufacturerList,
  getModelList,
  getTenantList,
  getUserList,
} from '#/graphql/queries';

const columns: ColumnProps<WorkOrderTemplate>[] = [
  {
    dataField: 'displayNumber',
    caption: 'Number',
    visible: true,
    openOnClick: true,
    fixed: true,
    dataType: 'string',
    sortIndex: 0,
    sortOrder: 'desc',
  },
  {
    dataField: 'title',
    caption: 'Title',
    visible: true,
    openOnClick: true,
    dataType: 'string',
  },
  {
    dataField: 'manufacturer.name',
    caption: 'Asset Manufacturer',
    visible: true,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getManufacturerList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'assetType.name',
    caption: 'Asset Type',
    visible: true,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getAssetTypeList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'model.name',
    caption: 'Asset Model',
    visible: true,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getModelList),
      displayExpr: 'name',
    },
  },

  { dataField: 'id', caption: 'ID', visible: false, showInColumnChooser: false },
  {
    dataField: 'description',
    caption: 'Description',
    dataType: 'string',
  },
  {
    dataField: 'category',
    caption: 'Category',
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: categories,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'notes',
    caption: 'Notes',
    dataType: 'string',
  },
  {
    dataField: 'estimatedCostValue',
    caption: 'Estimated Cost',
    dataType: 'number',
    format: {
      type: 'currency',
      precision: 2,
    },
  },
  {
    dataField: 'estimatedEffort',
    caption: 'Estimated Effort',
    dataType: 'number',
    calcDisplayValue: (row) =>
      !!row.estimatedEffort && row.estimatedEffort > 0
        ? `${row.estimatedEffort} ${row.estimatedEffort === 1 ? 'hour' : 'hours'}`
        : '',
  },
  {
    dataField: 'impact',
    caption: 'Impact',
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: impacts,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'priority',
    caption: 'Priority',
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: priorities,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'type',
    caption: 'Work Order Type',
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: types,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'executionInterval',
    caption: 'Execution Interval',
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: executionIntervals,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'tenant.name',
    caption: 'Organization',
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getTenantList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'createdByUser.name',
    caption: 'Created By',
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getUserList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'createdAt',
    caption: 'Created At',
    dataType: 'date',
  },
  {
    dataField: 'updatedByUser.name',
    caption: 'Updated By',
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getUserList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'updatedAt',
    caption: 'Updated At',
    dataType: 'date',
  },
];

export default columns;
